import React, { useEffect, useState } from "react";
import { NavLink, Routes, Route, useNavigate, Link, Outlet } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { api, formatApiError, API } from "../lib/api";
import { toast } from "sonner";
import { Calendar, ClipboardList, FileText, Target, LogOut, Upload, Trash2, Plus, ArrowRight, Check, Settings as SettingsIcon } from "lucide-react";
import Raters360 from "../components/Raters360";
function Shell({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const nav = useNavigate();

  const links = [
    { to: "/dashboard", label: t.dashboard.nav.overview, icon: ClipboardList, exact: true },
    { to: "/dashboard/tests", label: t.dashboard.nav.tests, icon: ClipboardList },
    { to: "/dashboard/sessions", label: t.dashboard.nav.sessions, icon: Calendar },
    { to: "/dashboard/objectives", label: t.dashboard.nav.objectives, icon: Target },
    { to: "/dashboard/documents", label: t.dashboard.nav.documents, icon: FileText },
    { to: "/dashboard/parametres", label: t.dashboard.nav.settings || "Paramètres", icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen flex bg-ivory">
      <aside className="hidden md:flex flex-col w-72 border-r border-black/10 bg-white p-8 sticky top-0 h-screen">
        <div className="mb-10">
          <div className="font-serif text-2xl text-navy font-light leading-tight">{user?.first_name ? `${user.first_name} ` : ""}{user?.name}</div>
          <div className="text-xs text-ink/50 mt-1">{user?.email}</div>
          {(user?.function || user?.company) && (
            <div className="mt-2 text-xs text-ink/65 leading-snug">
              {user?.function}
              {user?.function && user?.company && <span className="text-ink/30"> · </span>}
              {user?.company && <span className="text-brand">{user.company}</span>}
            </div>
          )}
          {user?.program && <div className="mt-3 text-xs text-ink/55 italic">{user.program}</div>}
        </div>

        <nav className="space-y-1 flex-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.exact}
              data-testid={`side-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-3 text-sm transition-colors ${isActive ? "bg-brand/10 text-brand border-l-2 border-brand" : "text-ink/70 hover:text-ink hover:bg-ivory border-l-2 border-transparent"}`}
            >
              <l.icon size={15} />
              {l.label}
            </NavLink>
          ))}
          {/* Admin link removed from coachee dashboard — admin access is via /acces-admin only */}
        </nav>

        <div className="mt-auto pt-8 border-t border-black/10">
          <div className="flex items-center gap-2 mb-4 font-mono text-[11px]">
            <button onClick={() => setLang("fr")} className={lang === "fr" ? "text-brand" : "text-ink/50"}>FR</button>
            <span className="text-ink/30">/</span>
            <button onClick={() => setLang("en")} className={lang === "en" ? "text-brand" : "text-ink/50"}>EN</button>
          </div>
          <button data-testid="dashboard-logout" onClick={async () => { await logout(); nav("/"); }} className="flex items-center gap-2 text-xs text-ink/60 hover:text-brand">
            <LogOut size={14} /> {t.nav.logout}
          </button>
          <div className="mt-6 text-[10px] text-ink/35 leading-relaxed">{t.dashboard.confidential}</div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="md:hidden flex items-center justify-between p-4 border-b border-black/10 bg-white">
          <Link to="/" className="text-sm font-serif text-navy">Audace &amp; Perspectives</Link>
          <button onClick={async () => { await logout(); nav("/"); }} className="text-xs">{t.nav.logout}</button>
        </div>
        <div className="p-6 md:p-12 max-w-[1200px] page-enter">{children}</div>
      </main>
    </div>
  );
}

function Overview() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/me/dashboard").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!data) return <div className="text-ink/50 font-mono text-xs uppercase tracking-[0.25em]">…</div>;
  const { stats, next_session } = data;

  const cards = [
    { v: stats.tests_pending, l: t.dashboard.stats.tests_pending, k: "tests-pending" },
    { v: stats.tests_completed, l: t.dashboard.stats.tests_completed, k: "tests-completed" },
    { v: stats.sessions_upcoming, l: t.dashboard.stats.sessions_upcoming, k: "sessions-upcoming" },
    { v: stats.objectives_in_progress, l: t.dashboard.stats.objectives_in_progress, k: "objectives-progress" },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 border border-black/10">
        {cards.map((c) => (
          <div key={c.k} data-testid={`stat-${c.k}`} className="bg-white p-6 md:p-8">
            <div className="num-display text-5xl text-navy leading-none">{c.v}</div>
            <div className="text-xs text-ink/60 mt-3 leading-tight">{c.l}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-7 bg-white border border-black/10 p-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand mb-4">— {t.dashboard.next_session}</div>
          {next_session ? (
            <div data-testid="next-session-card">
              <h3 className="font-serif text-2xl font-light text-navy">{next_session.title}</h3>
              <div className="mt-2 text-ink/65 text-sm">{new Date(next_session.starts_at).toLocaleString()}</div>
              {next_session.location && <div className="mt-1 text-ink/60 text-sm">{next_session.location}</div>}
              {next_session.notes && <p className="mt-4 text-ink/70 text-sm leading-relaxed">{next_session.notes}</p>}
            </div>
          ) : (
            <div className="text-ink/50">{t.dashboard.no_session}</div>
          )}
        </div>
        <div className="col-span-12 md:col-span-5 bg-navy text-ivory p-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand mb-4">— {t.dashboard.stats.progression}</div>
          <div className="num-display text-7xl text-ivory leading-none">{stats.progression}<span className="text-3xl text-brand">%</span></div>
          <div className="mt-6 h-1 bg-ivory/10 overflow-hidden">
            <div className="h-full bg-brand transition-all duration-1000" style={{ width: `${stats.progression}%` }}></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TestsList() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  useEffect(() => { api.get("/me/tests").then(({ data }) => setItems(data)); }, []);
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand mb-4">— Tests</div>
      <h1 className="font-serif text-4xl md:text-5xl font-light text-navy mb-12">{t.dashboard.nav.tests}</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10">
        {items
          .filter((it) => !(it.template?.id === "360-competencies" && it.status === "completed"))
          .map((it) => {
            const is360 = it.template?.id === "360-competencies";
            return (
          <div key={it.id} data-testid={`test-card-${it.id}`} className="bg-white p-8 flex flex-col gap-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand mb-2">{is360 ? (lang === "fr" ? "Auto-évaluation" : "Self-assessment") : it.template?.category}</div>
                <h3 className="font-serif text-2xl font-light text-navy max-w-md">{lang === "fr" ? it.template?.title_fr : it.template?.title_en}</h3>
              </div>
              {it.status === "completed" ? (
                <span className="text-xs flex items-center gap-1 text-brand"><Check size={14} /> {t.dashboard.test_completed}</span>
              ) : (
                <span className="text-xs text-ink/40 uppercase font-mono">{it.template?.duration_minutes} min</span>
              )}
            </div>
            <p className="text-ink/65 text-sm">{lang === "fr" ? it.template?.description_fr : it.template?.description_en}</p>
            {it.status === "completed" && it.result && !is360 && (
              <div className="border-t border-black/10 pt-4 grid grid-cols-2 gap-4 text-sm">
                <div><div className="text-[10px] uppercase tracking-[0.2em] text-ink/50">{t.dashboard.result_score}</div><div className="font-serif text-xl text-navy">{it.result.average}</div></div>
                <div><div className="text-[10px] uppercase tracking-[0.2em] text-ink/50">{t.dashboard.result_level}</div><div className="font-serif text-xl text-brand">{lang === "fr" ? it.result.level_fr : it.result.level_en}</div></div>
              </div>
            )}
            <div className="flex items-center gap-5">
              <button data-testid={`test-cta-${it.id}`} onClick={() => nav(`/dashboard/tests/${it.id}`)} className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.18em] font-mono text-brand hover:text-brandDark">
                {it.status === "completed" ? t.dashboard.review_test : t.dashboard.take_test} <ArrowRight size={14} />
              </button>
              {it.status === "completed" && !is360 && (
                <a
                  data-testid={`test-pdf-${it.id}`}
                  href={`${API}/me/tests/${it.id}/pdf?lang=${lang}`}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-mono text-ink/60 hover:text-brand"
                  target="_blank" rel="noreferrer"
                >
                  PDF
                </a>
              )}
            </div>
          </div>
            );
          })}
      </div>
    </div>
  );
}

function TestDetail() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const id = window.location.pathname.split("/").pop();
  const [assignment, setAssignment] = useState(null);
  const [answers, setAnswers] = useState({});
  const [bonus, setBonus] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/me/tests/${id}`).then(({ data }) => {
      setAssignment(data);
      if (data.answers) {
        setAnswers(data.answers);
        if (Array.isArray(data.answers.__bonus__)) setBonus(data.answers.__bonus__);
      }
    }).catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  if (!assignment) return <div className="text-ink/50">…</div>;
  const tpl = assignment.template;
  const done = assignment.status === "completed";
  const setAnswer = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));
  const toggleBonus = (qid) => {
    if (bonus.includes(qid)) setBonus(bonus.filter((b) => b !== qid));
    else if (bonus.length < (tpl.bonus_select || 3)) setBonus([...bonus, qid]);
    else toast.warning(lang === "fr" ? `${tpl.bonus_select} maximum` : `${tpl.bonus_select} max`);
  };

  const submit = async () => {
    // Validation with missing-line popup (NE = "NE" string is a valid answer)
    const missingNumbers = [];
    tpl.questions.forEach((q, idx) => {
      const a = answers[q.id];
      if (a === undefined || a === null || a === "") missingNumbers.push(idx + 1);
    });
    if (missingNumbers.length) {
      const numbersText = missingNumbers.join(", ");
      window.alert(
        lang === "fr"
          ? `Questionnaire incomplet — merci de répondre à toutes les lignes (utilisez « NE » si une dimension n'est pas évaluable).\n\nLignes à compléter : ${numbersText}`
          : `Incomplete questionnaire — please answer every row (use "NE" if a dimension is not assessable).\n\nMissing rows: ${numbersText}`
      );
      // Scroll the first missing question into view
      const firstId = tpl.questions[missingNumbers[0] - 1].id;
      const el = document.getElementById(`q-${firstId}`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (tpl.bonus_select && bonus.length !== tpl.bonus_select) {
      return toast.error(lang === "fr"
        ? `Sélectionnez ${tpl.bonus_select} items bonus (actuellement ${bonus.length})`
        : `Pick ${tpl.bonus_select} bonus items (currently ${bonus.length})`);
    }
    setSubmitting(true);
    try {
      const payload = tpl.bonus_select ? { ...answers, __bonus__: bonus } : answers;
      const { data } = await api.post(`/me/tests/${id}/submit`, { answers: payload });
      const label = data.result?.level_fr || data.result?.score || "";
      toast.success(`${t.dashboard.test_completed} — ${label}`);
      nav("/dashboard/tests");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  // Group questions by category for the 360 grid
  const questionsByDim = {};
  if (tpl.multi_dimension && tpl.dimensions && !tpl.binary_choice) {
    for (const q of tpl.questions) {
      const d = q.dim || q.axis || "_";
      (questionsByDim[d] = questionsByDim[d] || []).push(q);
    }
  }

  const renderScale = (q, idx) => {
    const scale = tpl.scale;
    const labels = lang === "fr" ? (q.options_fr || scale.labels_fr) : (q.options_en || scale.labels_en);
    return (
      <div className={`grid gap-px bg-black/5 ml-8`} style={{ gridTemplateColumns: `${tpl.allow_skip ? "44px " : ""}repeat(${scale.max - scale.min + 1}, 1fr)` }}>
        {tpl.allow_skip && (
          <button
            disabled={done}
            data-testid={`q-${q.id}-NE`}
            onClick={() => setAnswer(q.id, "NE")}
            className={`p-1.5 text-center transition-all border ${answers[q.id] === "NE" ? "bg-navy text-white border-navy" : "bg-white border-transparent text-ink/55 hover:bg-ivory hover:text-navy hover:border-navy/30"} ${done ? "opacity-70" : ""}`}
            title={lang === "fr" ? "Non évaluable" : "Not evaluable"}
          >
            <div className="font-mono text-[11px] font-semibold">NE</div>
          </button>
        )}
        {Array.from({ length: scale.max - scale.min + 1 }, (_, i) => {
          const v = scale.min + i;
          const selected = answers[q.id] === v;
          // Color tint per scale position for visual depth
          const tint = ["bg-rose-50", "bg-amber-50", "bg-yellow-50", "bg-lime-50", "bg-emerald-50", "bg-emerald-100"][Math.min(i, 5)];
          return (
            <button
              key={v}
              disabled={done}
              data-testid={`q-${q.id}-${v}`}
              onClick={() => setAnswer(q.id, v)}
              className={`p-1.5 text-center transition-all border ${selected ? "bg-brand text-white border-brand scale-105 shadow-sm" : `${tint} border-transparent hover:border-brand/40 text-ink/80`} ${done ? "opacity-70" : ""}`}
            >
              <div className="num-display text-base leading-none">{v}</div>
              {labels && labels[i] && <div className="text-[8px] text-current/75 mt-0.5 uppercase tracking-wide leading-tight truncate">{labels[i]}</div>}
            </button>
          );
        })}
      </div>
    );
  };

  const renderBinary = (q, idx) => (
    <div className="grid grid-cols-2 gap-2 ml-8">
      {["A", "B"].map((opt) => {
        const selected = answers[q.id] === opt;
        const txt = lang === "fr" ? q[`${opt.toLowerCase()}_fr`] : q[`${opt.toLowerCase()}_en`];
        return (
          <button
            key={opt}
            disabled={done}
            data-testid={`q-${q.id}-${opt}`}
            onClick={() => setAnswer(q.id, opt)}
            className={`p-3 text-left transition-all border ${selected ? "bg-brand text-white border-brand shadow-sm" : "bg-ivory hover:bg-white border-black/10 hover:border-brand/40"} ${done ? "opacity-70" : ""}`}
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] mb-1 opacity-70">{opt}</div>
            <div className="text-xs md:text-sm leading-snug">{txt}</div>
          </button>
        );
      })}
    </div>
  );

  const renderQuestion = (q, i, dimMeta) => {
    const zebra = i % 2 === 1 ? "bg-ivory/40" : "bg-white";
    return (
      <div key={q.id} id={`q-${q.id}`} className={`px-4 md:px-5 py-3 ${zebra}`} data-testid={`question-${q.id}`}>
        <div className="flex items-baseline gap-3 mb-2.5">
          <span className="font-mono text-[10px] text-brand font-semibold w-6 flex-none">{String(i + 1).padStart(2, "0")}</span>
          <p className="flex-1 text-sm text-ink font-light leading-snug">{lang === "fr" ? (q.fr || q.title_fr || "") : (q.en || q.title_en || "")}</p>
          {tpl.bonus_select && !done && (
            <button
              data-testid={`bonus-${q.id}`}
              onClick={() => toggleBonus(q.id)}
              title={lang === "fr" ? "Marquer comme bonus" : "Mark as bonus"}
              className={`flex-none w-7 h-7 border text-[12px] font-mono transition-all ${bonus.includes(q.id) ? "bg-brand text-white border-brand" : "border-black/15 text-ink/45 hover:border-brand hover:text-brand"}`}
            >
              {bonus.includes(q.id) ? "★" : "+"}
            </button>
          )}
        </div>
        {tpl.binary_choice ? renderBinary(q, i) : renderScale(q, i)}
      </div>
    );
  };

  return (
    <div data-testid="test-detail">
      <button onClick={() => nav("/dashboard/tests")} className="text-xs text-ink/50 hover:text-brand mb-6">← {t.dashboard.nav.tests}</button>
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand mb-3">— {tpl.category}</div>
      <h1 className="font-serif text-4xl md:text-5xl font-light text-navy leading-tight">{lang === "fr" ? tpl.title_fr : tpl.title_en}</h1>
      <p className="mt-4 text-ink/70 max-w-2xl">{lang === "fr" ? tpl.description_fr : tpl.description_en}</p>

      {tpl.id === "360-competencies" && (
        <Raters360 assignmentId={assignment.id} />
      )}

      {tpl.bonus_select && !done && (
        <div className="mt-10 bg-brand/5 border border-brand/30 p-5 text-sm" data-testid="bonus-hint">
          <strong className="text-brand">{lang === "fr" ? "Étape bonus" : "Bonus step"} :</strong>{" "}
          {lang === "fr"
            ? `cliquez sur l'étoile à droite de chaque énoncé pour marquer les ${tpl.bonus_select} items qui vous correspondent le plus (+${tpl.bonus_points} pts chacun). Sélectionnés : ${bonus.length}/${tpl.bonus_select}.`
            : `Click the star next to each statement to mark the ${tpl.bonus_select} items you most resonate with (+${tpl.bonus_points} pts each). Selected: ${bonus.length}/${tpl.bonus_select}.`}
        </div>
      )}

      {/* Categorized 360 questions — hidden once auto-evaluation submitted */}
      {tpl.id === "360-competencies" ? (
        done ? (
          <div className="mt-10 bg-brand/5 border-l-2 border-brand p-5 text-sm" data-testid="360-completed-notice">
            <strong className="text-brand">{lang === "fr" ? "Auto-évaluation complétée" : "Self-assessment completed"}</strong>{" "}
            — {lang === "fr"
              ? "merci. La synthèse globale sera partagée par votre coach lors d'une prochaine séance."
              : "thank you. The global synthesis will be shared by your coach in an upcoming session."}
          </div>
        ) : (
          <div className="mt-10 space-y-10">
          {Object.entries(tpl.dimensions).map(([dimId, dimMeta]) => {
            const qs = questionsByDim[dimId] || [];
            if (qs.length === 0) return null;
            return (
              <div key={dimId} data-testid={`section-${dimId}`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand mb-4">— {lang === "fr" ? dimMeta.fr : dimMeta.en}</div>
                <div className="divide-y divide-black/10 bg-white border border-black/10">
                  {qs.map((q, i) => renderQuestion(q, tpl.questions.indexOf(q), dimMeta))}
                </div>
              </div>
            );
          })}
        </div>
        )
      ) : (
        <div className="mt-10 divide-y divide-black/10 bg-white border border-black/10">
          {tpl.questions.map((q, i) => renderQuestion(q, i, null))}
        </div>
      )}

      {!done && (
        <button data-testid="submit-test" onClick={submit} disabled={submitting} className="mt-10 bg-brand hover:bg-brandDark text-white px-8 py-5 text-sm uppercase tracking-[0.16em] font-mono">{submitting ? "..." : t.dashboard.submit_test}</button>
      )}

      {done && assignment.result && tpl.id !== "360-competencies" && (
        <div className="mt-10 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10 border border-black/10">
            <div className="bg-white p-6"><div className="text-[10px] uppercase tracking-[0.2em] text-ink/50">{t.dashboard.result_score}</div><div className="num-display text-4xl text-navy">{assignment.result.average || assignment.result.score}</div></div>
            <div className="bg-white p-6"><div className="text-[10px] uppercase tracking-[0.2em] text-ink/50">%</div><div className="num-display text-4xl text-navy">{assignment.result.percentage}</div></div>
            <div className="bg-brand text-white p-6"><div className="text-[10px] uppercase tracking-[0.2em] text-white/80">{t.dashboard.result_level}</div><div className="font-serif text-xl leading-tight">{lang === "fr" ? assignment.result.level_fr : assignment.result.level_en}</div></div>
          </div>
          {assignment.result.dimensions && assignment.result.dimensions.length > 0 && (
            <div className="bg-white border border-black/10 p-6 md:p-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand mb-6">— {lang === "fr" ? "Profil par dimension" : "Profile by dimension"}</div>
              <div className="space-y-5">
                {assignment.result.dimensions.map((d, idx) => (
                  <div key={d.id} data-testid={`dim-${d.id}`}>
                    <div className="flex items-baseline justify-between mb-2 gap-4">
                      <div className="min-w-0">
                        <span className="font-mono text-[10px] text-brand mr-2">0{idx + 1}</span>
                        <span className="font-serif text-lg text-navy">{lang === "fr" ? d.label_fr : d.label_en}</span>
                        {d.activated && <span className="ml-3 text-[10px] uppercase tracking-[0.18em] font-mono text-brand">★ {lang === "fr" ? "activé" : "active"}</span>}
                      </div>
                      <span className="num-display text-xl text-navy flex-none">
                        {d.total != null ? d.total : d.average}
                        <span className="text-xs text-ink/50">{d.total != null ? "" : ` / ${tpl.scale.max}`}</span>
                      </span>
                    </div>
                    <div className="h-1 bg-black/5 overflow-hidden">
                      <div className="h-full bg-brand transition-all duration-1000" style={{ width: `${d.percentage}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Sessions() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/me/sessions").then(({ data }) => setItems(data)); }, []);
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand mb-4">— Agenda</div>
      <h1 className="font-serif text-4xl md:text-5xl font-light text-navy mb-12">{t.dashboard.nav.sessions}</h1>
      <div className="divide-y divide-black/10 border border-black/10 bg-white">
        {items.length === 0 && <div className="p-8 text-ink/50">{t.dashboard.no_session}</div>}
        {items.map((s) => (
          <div key={s.id} className="p-6 md:p-8 grid grid-cols-12 gap-6 items-start" data-testid={`session-${s.id}`}>
            <div className="col-span-12 md:col-span-3">
              <div className="num-display text-3xl text-navy">{new Date(s.starts_at).toLocaleDateString(undefined, { day: "2-digit", month: "short" })}</div>
              <div className="text-sm text-ink/60">{new Date(s.starts_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              <div className="text-xs text-ink/50 mt-1">{s.duration_minutes} min</div>
            </div>
            <div className="col-span-12 md:col-span-9">
              <h3 className="font-serif text-2xl text-navy font-light">{s.title}</h3>
              {s.location && <div className="text-sm text-ink/60 mt-1">{s.location}</div>}
              {s.notes && <p className="mt-3 text-ink/70 text-sm">{s.notes}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Objectives() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", target_date: "", status: "todo", progress: 0 });

  const load = () => api.get("/me/objectives").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/me/objectives", form); setAdding(false); setForm({ title: "", description: "", target_date: "", status: "todo", progress: 0 }); load(); toast.success("Objectif créé"); }
    catch (e2) { toast.error(formatApiError(e2)); }
  };

  const updateProgress = async (o, p) => {
    let status = "todo";
    if (p >= 100) status = "done";
    else if (p > 0) status = "in_progress";
    await api.patch(`/me/objectives/${o.id}`, { ...o, progress: p, status });
    load();
  };

  const remove = async (id) => { await api.delete(`/me/objectives/${id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-12">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand mb-4">— Cap</div>
          <h1 className="font-serif text-4xl md:text-5xl font-light text-navy">{t.dashboard.nav.objectives}</h1>
        </div>
        <button data-testid="add-objective" onClick={() => setAdding(true)} className="inline-flex items-center gap-2 bg-brand hover:bg-brandDark text-white px-5 py-3 text-xs uppercase tracking-[0.16em] font-mono">
          <Plus size={14} /> {t.dashboard.new_objective}
        </button>
      </div>

      {adding && (
        <form onSubmit={save} data-testid="objective-form" className="border border-black/10 bg-white p-8 mb-8 grid grid-cols-12 gap-6">
          <input required placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" />
          <input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" />
          <textarea placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="col-span-12 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand resize-none" />
          <div className="col-span-12 flex gap-3">
            <button type="submit" className="bg-brand text-white px-5 py-3 text-xs uppercase tracking-[0.16em] font-mono">{t.dashboard.save}</button>
            <button type="button" onClick={() => setAdding(false)} className="border border-ink/20 px-5 py-3 text-xs uppercase tracking-[0.16em] font-mono">{t.dashboard.cancel}</button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {items.map((o) => (
          <div key={o.id} className="border border-black/10 bg-white p-6 md:p-8" data-testid={`objective-${o.id}`}>
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1">
                <h3 className="font-serif text-2xl font-light text-navy">{o.title}</h3>
                {o.description && <p className="mt-2 text-ink/70 text-sm">{o.description}</p>}
                {o.target_date && <div className="mt-2 text-xs text-ink/50 font-mono uppercase tracking-wider">→ {new Date(o.target_date).toLocaleDateString()}</div>}
              </div>
              <button data-testid={`delete-obj-${o.id}`} onClick={() => remove(o.id)} className="text-ink/40 hover:text-destructive"><Trash2 size={16} /></button>
            </div>
            <div className="mt-5">
              <div className="flex items-center justify-between text-xs text-ink/60 mb-2">
                <span className="font-mono uppercase tracking-wider">{t.dashboard.progress}</span>
                <span className="num-display text-base text-navy">{o.progress}%</span>
              </div>
              <input type="range" min="0" max="100" step="5" value={o.progress} onChange={(e) => updateProgress(o, parseInt(e.target.value))} className="w-full accent-brand" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Documents() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/me/documents").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Max 10 Mo");
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = String(reader.result).split(",")[1];
      try {
        await api.post("/me/documents", { name: file.name, mime_type: file.type, content_base64: b64 });
        toast.success("Document ajouté");
        load();
      } catch (e2) { toast.error(formatApiError(e2)); }
      finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const download = async (d) => {
    const { data } = await api.get(`/me/documents/${d.id}`);
    const a = document.createElement("a");
    a.href = `data:${data.mime_type};base64,${data.content_base64}`;
    a.download = data.name;
    a.click();
  };

  const remove = async (id) => { await api.delete(`/me/documents/${id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-12">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand mb-4">— Archives</div>
          <h1 className="font-serif text-4xl md:text-5xl font-light text-navy">{t.dashboard.nav.documents}</h1>
        </div>
        <label data-testid="upload-doc" className="inline-flex items-center gap-2 bg-brand hover:bg-brandDark text-white px-5 py-3 text-xs uppercase tracking-[0.16em] font-mono cursor-pointer">
          <Upload size={14} /> {uploading ? "..." : t.dashboard.upload_doc}
          <input type="file" onChange={upload} className="hidden" />
        </label>
      </div>

      <div className="divide-y divide-black/10 border border-black/10 bg-white">
        {items.length === 0 && <div className="p-8 text-ink/50">—</div>}
        {items.map((d) => (
          <div key={d.id} className="p-5 md:p-6 flex items-center justify-between gap-6" data-testid={`doc-${d.id}`}>
            <div className="flex items-center gap-4 min-w-0">
              <FileText size={18} className="text-brand flex-none" />
              <div className="min-w-0">
                <div className="text-sm text-ink font-medium truncate">{d.name}</div>
                <div className="text-xs text-ink/50">{(d.size_bytes / 1024).toFixed(1)} ko · {new Date(d.created_at).toLocaleDateString()}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => download(d)} className="text-xs uppercase tracking-[0.16em] font-mono text-brand hover:text-brandDark">⬇</button>
              <button data-testid={`delete-doc-${d.id}`} onClick={() => remove(d.id)} className="text-ink/40 hover:text-destructive"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Settings() {
  const { t, lang } = useLang();
  const { user, refresh } = useAuth();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [func, setFunc] = useState(user?.function || "");
  const [funcSaving, setFuncSaving] = useState(false);
  const [genderSel, setGenderSel] = useState(user?.gender || "");
  const [firstNameSel, setFirstNameSel] = useState(user?.first_name || "");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) return setErr(t.auth.mismatch);
    if (pw.length < 8) return setErr(t.auth.too_short || "Min 8 caractères");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success(t.auth.change_done || "Mot de passe mis à jour");
      setCur(""); setPw(""); setPw2("");
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setLoading(false); }
  };

  const saveFunc = async (e) => {
    e.preventDefault();
    setFuncSaving(true);
    try {
      await api.patch("/me", { function: func, gender: genderSel || null, first_name: firstNameSel || null });
      toast.success(lang === "fr" ? "Fonction mise à jour" : "Function updated");
      if (refresh) await refresh();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setFuncSaving(false); }
  };

  return (
    <div data-testid="settings-page">
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand mb-4">— {t.dashboard.nav.settings || "Paramètres"}</div>
      <h1 className="font-serif text-4xl md:text-5xl font-light text-navy mb-12">{lang === "fr" ? "Paramètres" : "Settings"}</h1>

      <form onSubmit={saveFunc} data-testid="settings-profile-form" className="bg-white border border-black/10 p-8 md:p-10 max-w-xl space-y-7 mb-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand">— {lang === "fr" ? "Profil" : "Profile"}</div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] font-mono text-ink/55 mb-2">{lang === "fr" ? "Genre" : "Title"}</label>
          <select data-testid="settings-gender" value={genderSel} onChange={(e) => setGenderSel(e.target.value)} className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand">
            <option value="">{lang === "fr" ? "Non renseigné" : "Not set"}</option>
            <option value="madame">{lang === "fr" ? "Madame" : "Ms."}</option>
            <option value="monsieur">{lang === "fr" ? "Monsieur" : "Mr."}</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] font-mono text-ink/55 mb-2">{lang === "fr" ? "Prénom" : "First name"}</label>
          <input data-testid="settings-first-name" value={firstNameSel} onChange={(e) => setFirstNameSel(e.target.value)} className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] font-mono text-ink/55 mb-2">{lang === "fr" ? "Entreprise" : "Company"}</label>
          <div className="w-full border-b border-ink/10 py-2 text-ink/65" data-testid="settings-company">{user?.company || <span className="italic text-ink/40">{lang === "fr" ? "Non renseignée — gérée par votre coach" : "Not set — managed by your coach"}</span>}</div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] font-mono text-ink/55 mb-2">{lang === "fr" ? "Fonction" : "Function"}</label>
          <input data-testid="settings-function" value={func} onChange={(e) => setFunc(e.target.value)} placeholder={lang === "fr" ? "Ex : Directeur·rice Financier·ère" : "e.g. CFO"} className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" />
        </div>
        <button type="submit" disabled={funcSaving} data-testid="settings-profile-save" className="bg-brand hover:bg-brandDark disabled:opacity-60 text-white px-7 py-4 text-xs uppercase tracking-[0.16em] font-mono">
          {(() => {
            if (funcSaving) return "...";
            return lang === "fr" ? "Enregistrer" : "Save";
          })()}
        </button>
      </form>

      <h2 className="font-serif text-2xl font-light text-navy mb-6">{lang === "fr" ? "Changer mon mot de passe" : "Change my password"}</h2>
      <form onSubmit={submit} data-testid="settings-change-form" className="bg-white border border-black/10 p-8 md:p-10 max-w-xl space-y-7">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] font-mono text-ink/55 mb-2">{t.auth.current_password}</label>
          <input data-testid="settings-current" required type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand transition-colors" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] font-mono text-ink/55 mb-2">{t.auth.new_password}</label>
          <input data-testid="settings-new" required type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand transition-colors" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] font-mono text-ink/55 mb-2">{t.auth.confirm_password}</label>
          <input data-testid="settings-confirm" required type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand transition-colors" />
        </div>
        {err && <div data-testid="settings-error" className="text-sm text-destructive">{err}</div>}
        <button data-testid="settings-submit" disabled={loading} type="submit" className="bg-brand hover:bg-brandDark disabled:opacity-60 text-white px-7 py-4 text-xs uppercase tracking-[0.16em] font-mono">
          {loading ? "..." : (t.auth.change_submit || "Mettre à jour")}
        </button>
      </form>
    </div>
  );
}

export default function Dashboard() {
  return (
    <Shell>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="tests" element={<TestsList />} />
        <Route path="tests/:id" element={<TestDetail />} />
        <Route path="sessions" element={<Sessions />} />
        <Route path="objectives" element={<Objectives />} />
        <Route path="documents" element={<Documents />} />
        <Route path="parametres" element={<Settings />} />
      </Routes>
    </Shell>
  );
}
